/**
 * Scroll Lock
 *
 * Locks background page scrolling for the two overlay signals Impulse puts on
 * <html>: `js-drawer-open` (nav, cart, collection filters, store availability,
 * header search) and `modal-open` (every theme.Modals consumer — JRNY, smartwatch,
 * protection plan, video, waitlist, quick shop — plus the <tool-tip>
 * sheet, which sets that class itself).
 *
 * It keys off those two classes, not off being an overlay, so an overlay that
 * emits neither is *not* covered. `snippets/overlay.lightbox.liquid` (the Archetype
 * flex-PDP image lightbox) is the live example: it runs its own
 * `document.body.style.overflow` lock, which is inert on iOS.
 *
 * Why this exists: Impulse's own lock doesn't work on iOS. `Drawers.open()` calls
 * `theme.a11y.lockMobileScrolling()`, which adds `.lock-scroll` to <html>, but
 * theme.css then reverts it on iOS specifically:
 *
 *   @media (max-width: 768px) {
 *     .supports-touch.lock-scroll { overflow: hidden; }
 *     @supports (-webkit-touch-callout: none) {
 *       .supports-touch.lock-scroll { overflow: initial; }  // iOS only
 *     }
 *   }
 *
 * `.supports-touch.lock-scroll` (0,2,0) also outranks `.js-drawer-open` (0,1,0),
 * so the drawer's own `overflow: hidden` loses too — leaving iPhones with no lock
 * at all. The page scrolled behind the drawer, and because the gesture drove the
 * page, the drawer's inner scroll area felt dead.
 *
 * Modals are worse off still. `theme.Modals` adds `modal-open` to <html>, and the
 * only rule behind it is `@media (min-width: 769px) { .modal-open { overflow: hidden } }`
 * — so a phone gets no CSS lock at all, and on iOS `overflow: hidden` would not have
 * held anyway. Same two symptoms as the drawers: the page scrolled behind the modal,
 * and the modal's own scroll area felt dead.
 *
 * `overflow: hidden` is not enough on iOS Safari, so this pins the body with
 * `position: fixed` at its current offset and restores the exact scroll position
 * on close — the one approach iOS honors. Applied only on coarse-pointer devices:
 * desktop already locks correctly via Impulse's CSS, and pinning the body there
 * would collapse the scrollbar and shift the layout.
 *
 * Hooks the classes on <html> rather than individual drawers or modals, so every
 * overlay is covered by one observer — including any added later. Holding the lock
 * while *either* class is present is also what makes a handoff safe: a modal opened
 * from a drawer swaps one class for the other, and the body is never unpinned in
 * between, so there is no relock at a stale offset and no way to leave it pinned.
 */
const scrollLock = () => {
  // Kept inside the closure: this loads as a classic deferred script, so a top-level
  // const would sit in the global lexical scope and could collide with another asset.
  const LOCK_CLASSES = ['js-drawer-open', 'modal-open'];
  const root = document.documentElement;
  let lockedAt = null;

  // Desktop keeps Impulse's CSS lock; only touch devices need the body pin.
  const pointer = window.matchMedia('(pointer: coarse)');
  const needsLock = () => pointer.matches;

  const isOverlayOpen = () => LOCK_CLASSES.some((cls) => root.classList.contains(cls));

  const lock = () => {
    if (lockedAt !== null) return;
    lockedAt = window.scrollY;
    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${lockedAt}px`,
      left: '0',
      right: '0',
      width: '100%',
    });
  };

  const unlock = () => {
    if (lockedAt === null) return;
    Object.assign(document.body.style, {
      position: '',
      top: '',
      left: '',
      right: '',
      width: '',
    });
    // Restore instantly — a smooth scroll would visibly slide the page as the overlay
    // animates out. 'auto' is instant here because nothing sets a document-level
    // scroll-behavior, and it's safe below iOS 15.4, where 'instant' isn't in the
    // ScrollBehavior enum yet and would throw a TypeError.
    window.scrollTo({ top: lockedAt, behavior: 'auto' });
    lockedAt = null;
  };

  const sync = () => {
    if (!needsLock()) {
      unlock(); // pointer changed (e.g. tablet docked to a mouse) while locked
      return;
    }
    if (isOverlayOpen()) lock();
    else unlock();
  };

  new MutationObserver(sync).observe(root, {
    attributes: true,
    attributeFilter: ['class'],
  });

  // The class observer alone would miss a pointer flip (a tablet docked to a mouse)
  // with an overlay open, leaving the body pinned on a device that no longer needs it.
  // MediaQueryList only gained addEventListener in Safari 14, and this runs on iOS by
  // definition, so fall back to the deprecated addListener rather than throwing on init.
  if (pointer.addEventListener) pointer.addEventListener('change', sync);
  else pointer.addListener(sync);

  sync(); // an overlay can already be open on load (e.g. back-navigation)

  return { isLocked: () => lockedAt !== null };
};

if (!window.bfxScrollLock) {
  window.bfxScrollLock = scrollLock();
}
